const getAudioContextConstructor = (win: Window): typeof AudioContext | undefined =>
  (typeof AudioContext !== 'undefined' ? AudioContext : undefined) ??
  ((win as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext);

export async function playMessageDropletTone(
  audioContextRef: { current: AudioContext | null },
  win: Window,
): Promise<void> {
  const AudioContextConstructor = getAudioContextConstructor(win);
  if (!AudioContextConstructor) {
    return;
  }

  const audioContext =
    audioContextRef.current && audioContextRef.current.state !== 'closed'
      ? audioContextRef.current
      : new AudioContextConstructor();
  audioContextRef.current = audioContext;

  if (audioContext.state === 'suspended') {
    try {
      await audioContext.resume();
    } catch {
      return;
    }
  }

  const startAt = audioContext.currentTime + 0.01;
  const masterGain = audioContext.createGain();
  masterGain.connect(audioContext.destination);
  masterGain.gain.setValueAtTime(0.0001, startAt);
  masterGain.gain.exponentialRampToValueAtTime(0.14, startAt + 0.018);
  masterGain.gain.exponentialRampToValueAtTime(0.0001, startAt + 0.32);

  const mainOscillator = audioContext.createOscillator();
  mainOscillator.type = 'sine';
  mainOscillator.frequency.setValueAtTime(1020, startAt);
  mainOscillator.frequency.exponentialRampToValueAtTime(540, startAt + 0.22);

  const shimmerOscillator = audioContext.createOscillator();
  shimmerOscillator.type = 'triangle';
  shimmerOscillator.frequency.setValueAtTime(1620, startAt);
  shimmerOscillator.frequency.exponentialRampToValueAtTime(960, startAt + 0.18);

  const shimmerGain = audioContext.createGain();
  shimmerGain.gain.setValueAtTime(0.0001, startAt);
  shimmerGain.gain.exponentialRampToValueAtTime(0.045, startAt + 0.02);
  shimmerGain.gain.exponentialRampToValueAtTime(0.0001, startAt + 0.18);

  mainOscillator.connect(masterGain);
  shimmerOscillator.connect(shimmerGain);
  shimmerGain.connect(audioContext.destination);

  mainOscillator.start(startAt);
  shimmerOscillator.start(startAt + 0.012);
  mainOscillator.stop(startAt + 0.34);
  shimmerOscillator.stop(startAt + 0.2);
}

export async function showDesktopNotification(options: {
  title: string;
  body: string;
  tag: string;
  onClick?: () => void | Promise<void>;
}): Promise<boolean> {
  if (typeof Notification === 'undefined') {
    return false;
  }

  let permission = Notification.permission;
  if (permission === 'default') {
    try {
      permission = await Notification.requestPermission();
    } catch {
      return false;
    }
  }

  if (permission !== 'granted') {
    return false;
  }

  const notification = new Notification(options.title, {
    body: options.body,
    tag: options.tag,
    silent: true,
  });

  notification.onclick = () => {
    notification.close();
    void options.onClick?.();
  };

  return true;
}
